import { createApi } from "@reduxjs/toolkit/query/react";
import { apiBaseQuery } from "../utils/apiBaseQuery";
import { RefurbishedProductIndexInterface } from "../interfaces/apiV2/refurbished/RefurbishedProductIndexInterface";
import { RefurbishedFiltersInterface } from "../interfaces/apiV2/refurbished/RefurbishedFiltersInterface";
import { RefurbishedCountryInterface } from "../interfaces/apiV2/refurbished/RefurbishedCountriesInterface";
import { RefurbishedOrderHistoryInterface } from "../interfaces/apiV2/refurbished/RefurbishedOrderHistoryInterface";
import { RefurbishedPendingOrderInterface } from "../interfaces/apiV2/refurbished/RefurbishedPendingOrderInterface";
import {
    ConfirmRefurbishedOrderRequestInterface,
    RefurbishedConfirmedOrderInterface,
} from "../interfaces/apiV2/refurbished/RefurbishedConfirmOrderInterface";
import { MetaInterface } from "../interfaces/apiV2/shared/MetaInterface";
import { createQueryString } from "../utils/createQueryString";
import { RefurbishedProductDetailInterface } from "../interfaces/apiV2/refurbished/RefurbishedProductDetailInterface";
import { LanguageInterface } from "../interfaces/shared/LanguageInterface";
import { userApiSlice } from "./user";

export const refurbishedApiSlice = createApi({
    reducerPath: "refurbishedApi",
    baseQuery: apiBaseQuery(),
    tagTypes: [
        "RefurbishedProducts",
        "RefurbishedProduct",
        "RefurbishedFilters",
        "RefurbishedCart",
        "RefurbishedCountries",
        "RefurbishedOrderHistory",
    ],
    endpoints: (builder) => ({
        getRefurbishedProducts: builder.query<
            {
                data: RefurbishedProductIndexInterface[];
                meta: MetaInterface;
            },
            {
                items_per_page?: number;
                filter?: {
                    category_id?: number;
                    grade_id?: number;
                };
            }
        >({
            query: (params) => ({
                url: `apiV2/refurbished?${createQueryString(params)}`,
            }),
            providesTags: ["RefurbishedProducts"],
        }),
        getRefurbishedProductDetail: builder.query<
            { data: RefurbishedProductDetailInterface },
            { id: string; language: LanguageInterface | null }
        >({
            query: ({ id }) => ({
                url: `apiV2/refurbished/${id}`,
            }),
            providesTags: (_result, _error, { id }) => [
                { type: "RefurbishedProduct", id },
            ],
        }),
        getRefurbishedFilters: builder.query<
            { data: RefurbishedFiltersInterface },
            void
        >({
            query: () => ({
                url: "apiV2/refurbished/filters",
            }),
            providesTags: ["RefurbishedFilters"],
        }),
        getRefurbishedCountries: builder.query<
            { data: RefurbishedCountryInterface[] },
            void
        >({
            query: () => ({
                url: "apiV2/refurbished/countries",
            }),
            providesTags: ["RefurbishedCountries"],
        }),
        getPendingOrder: builder.query<
            { data: RefurbishedPendingOrderInterface | null },
            void
        >({
            query: () => ({
                url: "apiV2/refurbished/orders/pending",
            }),
            providesTags: ["RefurbishedCart"],
        }),
        getOrderHistory: builder.query<
            { data: RefurbishedOrderHistoryInterface[] },
            void
        >({
            query: () => ({
                url: "apiV2/refurbished/orders/history",
            }),
            providesTags: ["RefurbishedOrderHistory"],
        }),
        reserveRefurbishedProduct: builder.mutation<
            { data: RefurbishedPendingOrderInterface | null },
            number
        >({
            query: (productId) => ({
                url: `apiV2/refurbished/${productId}/reserve`,
                method: "POST",
            }),
            invalidatesTags: ["RefurbishedProducts", "RefurbishedProduct"],
            async onQueryStarted(_productId, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        refurbishedApiSlice.util.updateQueryData(
                            "getPendingOrder",
                            undefined,
                            () => data,
                        ),
                    );
                } catch {
                    // Failed reservations keep the existing cart cache.
                }
            },
        }),
        removeReservedRefurbishedProduct: builder.mutation<
            { data: RefurbishedPendingOrderInterface | null },
            number
        >({
            query: (productId) => ({
                url: `apiV2/refurbished/${productId}/reserve`,
                method: "DELETE",
            }),
            invalidatesTags: ["RefurbishedProducts", "RefurbishedProduct"],
            async onQueryStarted(_productId, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        refurbishedApiSlice.util.updateQueryData(
                            "getPendingOrder",
                            undefined,
                            () => data,
                        ),
                    );
                } catch {
                    // Failed removals keep the existing cart cache.
                }
            },
        }),
        confirmRefurbishedOrder: builder.mutation<
            { data: RefurbishedConfirmedOrderInterface },
            {
                orderId: number;
                payload: ConfirmRefurbishedOrderRequestInterface;
            }
        >({
            query: ({ orderId, payload }) => ({
                url: `apiV2/refurbished/orders/${orderId}/confirm`,
                method: "POST",
                body: payload,
            }),
            invalidatesTags: [
                "RefurbishedCart",
                "RefurbishedProducts",
                "RefurbishedOrderHistory",
            ],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(userApiSlice.util.invalidateTags(["User"]));
                } catch {
                    // Keep cached user data when order confirmation fails
                }
            },
        }),
        exportRefurbishedProduct: builder.mutation<
            { blob: Blob; contentType: string | null },
            number
        >({
            query: (productId) => ({
                url: `apiV2/refurbished/${productId}/export`,
                responseHandler: async (response: Response) => ({
                    blob: await response.blob(),
                    contentType: response.headers.get("content-type"),
                }),
            }),
        }),
    }),
});

export const {
    useGetRefurbishedProductsQuery,
    useGetRefurbishedProductDetailQuery,
    useGetRefurbishedFiltersQuery,
    useGetRefurbishedCountriesQuery,
    useGetPendingOrderQuery,
    useGetOrderHistoryQuery,
    useReserveRefurbishedProductMutation,
    useRemoveReservedRefurbishedProductMutation,
    useConfirmRefurbishedOrderMutation,
    useExportRefurbishedProductMutation,
} = refurbishedApiSlice;
